/**
 * Sprint 0150 — Visitor Approval "Ringer".
 *
 * Mounted at the top of the app shell. Polls
 * /visitors/pending-my-approval every 5s. When a new pending entry
 * arrives that the user hasn't already dismissed/decided:
 *   - plays a WebAudio "phone ring" tone in a loop
 *   - shows a full-screen modal with the visitor's photo, business
 *     card (if any), name, company, mobile, purpose
 *   - exposes Approve / Reject buttons
 *
 * No external audio asset — uses WebAudio API to generate two
 * alternating tones (440Hz + 480Hz) like a phone ringer.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert, Avatar, Button, Card, Descriptions, Input, Modal, Space,
  Tag, Typography, message,
} from 'antd'
import {
  CheckOutlined, CloseOutlined, PhoneFilled, UserOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { visitorsApi } from '@/features/gate/api'
import type { VisitorLog } from '@/shared/types/master'
import { useAuthStore } from '@/features/auth/store'
import { usePermission } from '@/shared/hooks/usePermission'

const { Text, Paragraph } = Typography

// ── WebAudio ringer ────────────────────────────────────────────────
class Ringer {
  private ctx: AudioContext | null = null
  private oscA: OscillatorNode | null = null
  private oscB: OscillatorNode | null = null
  private gain: GainNode | null = null
  private interval: number | null = null
  private playing = false

  start() {
    if (this.playing) return
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Ctx = (window.AudioContext || (window as any).webkitAudioContext)
      this.ctx = new Ctx()
      this.gain = this.ctx.createGain()
      this.gain.gain.value = 0.15
      this.gain.connect(this.ctx.destination)
      this.oscA = this.ctx.createOscillator()
      this.oscA.frequency.value = 440
      this.oscA.connect(this.gain)
      this.oscA.start()
      this.oscB = this.ctx.createOscillator()
      this.oscB.frequency.value = 480
      this.oscB.connect(this.gain)
      this.oscB.start()
      // Phone-ring envelope: ring 1s, pause 2s, repeat.
      let on = true
      const beat = () => {
        if (!this.gain) return
        this.gain.gain.cancelScheduledValues(this.ctx!.currentTime)
        this.gain.gain.setValueAtTime(on ? 0.15 : 0, this.ctx!.currentTime)
        on = !on
      }
      beat()
      this.interval = window.setInterval(beat, 1000)
      this.playing = true
    } catch {
      // Audio start can fail before user gesture; silently ignore.
    }
  }

  stop() {
    if (this.interval !== null) {
      window.clearInterval(this.interval)
      this.interval = null
    }
    try {
      this.oscA?.stop()
      this.oscB?.stop()
      this.ctx?.close()
    } catch {
      // ignore
    }
    this.oscA = null
    this.oscB = null
    this.gain = null
    this.ctx = null
    this.playing = false
  }
}

export default function VisitorApprovalRinger() {
  const qc = useQueryClient()
  const isAuthed = useAuthStore((s) => !!s.accessToken)
  // Only USERS WHO CAN APPROVE visitors should poll the pending queue.
  // Gate Operators, accountants, etc. don't have visitor.approve and
  // would otherwise hit a 403 toast every 5 seconds.
  const canApprove = usePermission('visitor.approve')
  const ringerRef = useRef<Ringer | null>(null)
  if (ringerRef.current === null) ringerRef.current = new Ringer()
  const [active, setActive] = useState<VisitorLog | null>(null)
  const dismissedRef = useRef<Set<string>>(new Set())
  const [rejecting, setRejecting] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  const { data } = useQuery({
    queryKey: ['my-pending-visitor-approvals'],
    queryFn: () => visitorsApi.listPendingMyApproval(),
    // Only poll when the user is logged in AND has the approve perm.
    // This prevents the 403-spam loop for Gate Operators, Accountants,
    // and every other role that's not a visitor host.
    enabled: isAuthed && canApprove,
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  })

  useEffect(() => {
    if (!data?.length) return
    if (active) return
    // Pick the oldest pending we haven't already dismissed
    const next = data.find((v) => !dismissedRef.current.has(v.id))
    if (next) {
      setActive(next)
      ringerRef.current?.start()
    }
  }, [data, active])

  // Stop ringing if visitor leaves the queue while modal is open
  useEffect(() => {
    if (active && data && !data.some((v) => v.id === active.id)) {
      ringerRef.current?.stop()
      setActive(null)
    }
  }, [data, active])

  const close = useCallback(() => {
    ringerRef.current?.stop()
    if (active) dismissedRef.current.add(active.id)
    setActive(null)
    setRejecting(false)
    setRejectReason('')
  }, [active])

  const approveMut = useMutation({
    mutationFn: (id: string) => visitorsApi.approve(id),
    onSuccess: () => {
      message.success('Visitor approved — gate notified to send them in.')
      qc.invalidateQueries({ queryKey: ['my-pending-visitor-approvals'] })
      qc.invalidateQueries({ queryKey: ['visitors'] })
      close()
    },
    onError: (e: { response?: { data?: { detail?: string } } }) => {
      message.error(e?.response?.data?.detail ?? 'Approve failed')
    },
  })

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      visitorsApi.reject(id, reason),
    onSuccess: () => {
      message.success('Visitor rejected — gate notified.')
      qc.invalidateQueries({ queryKey: ['my-pending-visitor-approvals'] })
      qc.invalidateQueries({ queryKey: ['visitors'] })
      close()
    },
    onError: (e: { response?: { data?: { detail?: string } } }) => {
      message.error(e?.response?.data?.detail ?? 'Reject failed')
    },
  })

  useEffect(() => () => ringerRef.current?.stop(), [])

  if (!active) return null

  return (
    <Modal
      open={!!active}
      title={
        <Space>
          <PhoneFilled style={{ color: '#1677ff', fontSize: 18 }} />
          <span>Visitor at the gate to meet you</span>
        </Space>
      }
      onCancel={() => {/* prevent close-by-X */}}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={
        rejecting ? (
          <Space>
            <Button onClick={() => { setRejecting(false); setRejectReason('') }}>
              Back
            </Button>
            <Button
              danger
              disabled={!rejectReason.trim()}
              loading={rejectMut.isPending}
              onClick={() =>
                rejectMut.mutate({ id: active.id, reason: rejectReason })
              }
            >
              Confirm Reject
            </Button>
          </Space>
        ) : (
          <Space size="middle">
            <Button
              danger
              size="large"
              icon={<CloseOutlined />}
              onClick={() => setRejecting(true)}
            >
              Reject
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<CheckOutlined />}
              loading={approveMut.isPending}
              onClick={() => approveMut.mutate(active.id)}
            >
              Approve & Send In
            </Button>
          </Space>
        )
      }
      width={560}
    >
      {active.escalated_at && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="This was escalated to you — original host didn't respond in time."
        />
      )}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space size="middle" align="start">
          <Avatar
            src={active.photo_url || undefined}
            icon={<UserOutlined />}
            size={96}
            shape="square"
            style={{ background: active.photo_url ? undefined : '#bfbfbf' }}
          />
          <div style={{ flex: 1 }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {active.visitor_name}
            </Typography.Title>
            <Text type="secondary">{active.visitor_company || 'No company on file'}</Text>
            <div style={{ marginTop: 6 }}>
              <Tag color="blue">{(active.purpose || '').replace(/_/g, ' ')}</Tag>
              <Tag>{active.visitor_pass_no}</Tag>
            </div>
          </div>
        </Space>
      </Card>
      <Descriptions size="small" column={1} colon bordered>
        <Descriptions.Item label="Mobile">
          {active.visitor_mobile
            ? <Text copyable>{active.visitor_mobile}</Text>
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Vehicle">{active.vehicle_number || '—'}</Descriptions.Item>
        <Descriptions.Item label="Items carried">{active.items_carried || '—'}</Descriptions.Item>
        <Descriptions.Item label="Requested at">
          {active.approval_requested_at
            ? dayjs(active.approval_requested_at).format('HH:mm:ss')
            : '—'}
          {active.approval_requested_at && (
            <Text type="secondary" style={{ marginLeft: 8 }}>
              ({dayjs(active.approval_requested_at).fromNow()})
            </Text>
          )}
        </Descriptions.Item>
        {active.remarks && (
          <Descriptions.Item label="Remarks">
            <Paragraph style={{ margin: 0, fontSize: 12 }}>{active.remarks}</Paragraph>
          </Descriptions.Item>
        )}
      </Descriptions>
      {rejecting && (
        <Input.TextArea
          rows={3}
          placeholder="Reason (mandatory) — e.g. Not expecting any visitors today."
          style={{ marginTop: 12 }}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      )}
    </Modal>
  )
}
